import React, { useEffect, useState } from "react";
import {
  ChevronRight,
  Plus,
  Trash2,
  Edit2,
  Search,
  Loader2,
  Grid,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Category, StatsApiResponse, Subcategory } from "../types";
import { getApiUrl } from "../config/api";
import { useApi } from "../context/ApiContext";
import { useStatusUpdate } from "../shared/hooks/useStatusUpdate";
import { DeleteConfirmationModal } from "../shared/components/DeleteConfirmationModal";
import { EditStatusModal } from "../shared/components/EditStatusModal";

const CategoryTable: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const navigate = useNavigate();
  const { refreshTrigger, isRefreshing } = useApi();
  const { updateStatus, apiLoading } = useStatusUpdate(
    "/api/templates/v1.0.0/templates-category"
  );

  const deleteCategory = (categoryId: string) =>
    updateStatus(categoryId, "Deleted");
  const toggleCategoryStatus = (category: Category) => {
    const newStatus = category.status === "Active" ? "Inactive" : "Active";
    return updateStatus(category.categoryId, newStatus);
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await fetch(getApiUrl("/api/templates/v1.0.0/stats"));
        const result: StatsApiResponse = await response.json();

        if (result.status === "success" && Array.isArray(result.data)) {
          setCategories(result.data);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [refreshTrigger]);

  // Navigate to subcategories
  const handleCategoryClick = (category: Category): void => {
    setSelectedCategory(category);
    navigate(`/sub-category/${category.categoryId}`, { state: { category } });
    setSearchTerm("");
  };

  // Handle delete with confirmation
  const handleDeleteClick = (item: Category | Subcategory): void => {
    if ("categoryId" in item) {
      setSelectedCategory(item);
    }
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedCategory) {
      const success = await deleteCategory(selectedCategory.categoryId);
      if (success) {
        setCategories((prev) =>
          prev.filter((cat) => cat.categoryId !== selectedCategory.categoryId)
        );
      }
    }
    setShowDeleteModal(false);
    setSelectedCategory(null);
  };

  const isDeleteConfirming = selectedCategory
    ? apiLoading === selectedCategory.categoryId
    : false;

  // Handle add category/subcategory
  const handleCreate = (): void => {
    navigate("/createCategory");
  };

  // Handle edit state toggle
  const handleEdit = (item: Category): void => {
    setSelectedCategory(item);
    setShowEditModal(true);
  };

  const confirmEdit = async () => {
    if (selectedCategory) {
      const success = await toggleCategoryStatus(selectedCategory);
      if (success) {
        const updated = categories.map((cat) =>
          cat.categoryId === selectedCategory.categoryId
            ? {
                ...cat,
                status: cat.status === "Active" ? "Inactive" : "Active",
              }
            : cat
        );
        setCategories(updated);
      }
    }
    setShowEditModal(false);
    setSelectedCategory(null);
  };

  const isEditConfirming = selectedCategory
    ? apiLoading === selectedCategory.categoryId
    : false;

  // Filter categories based on search
  const filteredCategories: Category[] = categories.filter((cat) =>
    cat.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Enhanced Header */}
      <div className="bg-white/80 backdrop-blur-xl shadow-lg border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Grid className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Category Management
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Manage your {categories.length} categories
                </p>
              </div>
            </div>

            <button
              onClick={handleCreate}
              className="group relative bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create Category</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-purple-700 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 -z-10"></div>
            </button>
          </div>

          {/* Enhanced Search Bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              className="w-full pl-12 pr-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading || isRefreshing ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading categories...</p>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 lg:gap-8">
            {filteredCategories.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No categories found
                </h3>
                <p className="text-gray-500">
                  {searchTerm
                    ? `No categories match "${searchTerm}"`
                    : "Get started by creating your first category"}
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredCategories.map((category: Category) => (
                  <button
                    key={category.categoryId}
                    type="button"
                    className="group bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/50 hover:border-blue-300/50 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden w-full text-left"
                    onClick={() => handleCategoryClick(category)}
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        {/* Category Info */}
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="relative">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                              {category.categoryName.charAt(0)}
                            </div>
                            {category.status === "Active" && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {category.categoryName}
                            </h3>
                            <p className="text-sm text-gray-500 mb-2">
                              ID: {category.categoryId.slice(-8)}
                            </p>
                            <div className="flex flex-wrap gap-3">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                {category.totalSubcategories} subcategories
                              </span>
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                {category.totalTemplates} templates
                              </span>
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  category.status === "Active"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {category.status}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-2 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(category);
                            }}
                            disabled={apiLoading === category.categoryId}
                            className="p-2.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group disabled:opacity-50"
                            aria-label="Edit category"
                          >
                            {apiLoading === category.categoryId ? (
                              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                            ) : (
                              <Edit2 className="w-4 h-4 text-blue-600" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(category);
                            }}
                            disabled={apiLoading === category.categoryId}
                            className="p-2.5 bg-red-50 hover:bg-red-100 rounded-lg transition-colors group disabled:opacity-50"
                            aria-label="Delete category"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                          <button
                            className="p-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label="View subcategories"
                          >
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal && !!selectedCategory}
        title="Delete Category"
        itemName={selectedCategory?.categoryName ?? ""}
        description="All associated subcategories and templates will also be affected."
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedCategory(null);
        }}
        isConfirming={isDeleteConfirming}
      />

      <EditStatusModal
        isOpen={showEditModal && !!selectedCategory}
        itemName={selectedCategory?.categoryName ?? ""}
        currentStatus={selectedCategory?.status ?? ""}
        newStatus={
          selectedCategory?.status === "Active" ? "Inactive" : "Active"
        }
        entityLabel="Update category status"
        primaryColor="blue"
        onConfirm={confirmEdit}
        onCancel={() => {
          setShowEditModal(false);
          setSelectedCategory(null);
        }}
        isConfirming={isEditConfirming}
      />
    </div>
  );
};

export default CategoryTable;
